import { useEffect, useRef, useState } from 'react'

interface UseDeferredVideoMountOptions {
	/** If true and prefers-reduced-motion is set, never mount the video. */
	skipOnReducedMotion?: boolean
}

interface UseDeferredVideoMountResult {
	/** Attach to the parent element you want to observe. */
	hostRef: React.RefObject<HTMLElement | null>
	/** True once: window.load fired AND the host is in/near the viewport. */
	shouldMount: boolean
	/** True after window.load. */
	pageLoaded: boolean
	/** True while the host is in/near the viewport. */
	inView: boolean
	/** True if the user has prefers-reduced-motion: reduce. */
	reducedMotion: boolean
}

/**
 * Hook that defers a "yes, mount the video now" decision until two
 * preconditions are met:
 *
 *   1. window 'load' has fired (image LCP paint cycle is complete).
 *   2. the host element is in the viewport (or within 200px of it).
 *
 * Use this for any heavy media that should never compete with critical
 * resources for bandwidth or paint time.
 */
export function useDeferredVideoMount<T extends HTMLElement>(
	options: UseDeferredVideoMountOptions = {},
): UseDeferredVideoMountResult & { hostRef: React.RefObject<T | null> } {
	const hostRef = useRef<T | null>(null)
	const [pageLoaded, setPageLoaded] = useState(false)
	const [inView, setInView] = useState(false)
	const [reducedMotion, setReducedMotion] = useState(false)

	useEffect(() => {
		if (typeof document === 'undefined') return
		if (document.readyState === 'complete') {
			setPageLoaded(true)
			return
		}
		const controller = new AbortController()
		window.addEventListener(
			'load',
			() => setPageLoaded(true),
			{ once: true, signal: controller.signal },
		)
		return () => controller.abort()
	}, [])

	useEffect(() => {
		const node = hostRef.current
		if (!node || typeof IntersectionObserver === 'undefined') return
		const observer = new IntersectionObserver(
			(entries) => {
				const entry = entries[0]
				if (!entry) return
				setInView(entry.isIntersecting)
			},
			{ rootMargin: '200px', threshold: 0 },
		)
		observer.observe(node)
		return () => observer.disconnect()
	}, [])

	useEffect(() => {
		if (typeof window === 'undefined' || !window.matchMedia) return
		const mql = window.matchMedia('(prefers-reduced-motion: reduce)')
		setReducedMotion(mql.matches)
		const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches)
		mql.addEventListener('change', handler)
		return () => mql.removeEventListener('change', handler)
	}, [])

	const shouldMount =
		pageLoaded && inView && !(options.skipOnReducedMotion && reducedMotion)

	return { hostRef, shouldMount, pageLoaded, inView, reducedMotion }
}

/** Build the standard Mux HLS URL for a playback id. */
export function getMuxHlsUrl(playbackId: string) {
	return `https://stream.mux.com/${playbackId}.m3u8`
}

/** Build the standard Mux poster URL for a playback id. */
export function getMuxPosterUrl(playbackId: string, width = 1200) {
	return `https://image.mux.com/${playbackId}/thumbnail.jpg?width=${width}&fit_mode=smartcrop`
}
